<template>
  <Modal
    :modal-types="[CHART_STYLE_BUTTON_MODAL]"
    font-awesome-icon-class="far fa-fw fa-bookmark"
    :modal-title="modalTitle"
  >
    <div
      v-show="isInitializingModal"
      class="loading-spinner-container"
    ><i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i></div>
    <form
      v-show="!isInitializingModal"
      class="chart-style-button-form"
      @submit.prevent="handleChartStyleButtonFormSubmit()"
    >
      <div class="chart-style-button-form-ui">
        <div class="ui-mini-containers">
          <div class="ui-mini-container">
            <label
              class="sc3-form-label"
              for="chart-style-button-modal-chart-style-button-position-select-menu"
            >POSITION</label>
            <br />
            <select
              id="chart-style-button-modal-chart-style-button-position-select-menu"
              class="sc3-select-menu"
              v-model="selectedChartStyleButtonIndex"
              :disabled="isSavingStyleButton || isClearingStyleButton"
            >
              <option
                :value="POSITION_OPTION_SELECT_A_POSITION"
                disabled
              >--</option>
              <option
                v-for="chartStyleButtonIndexOption in availableChartStyleButtonIndexOptions"
                :key="chartStyleButtonIndexOption.value"
                :value="chartStyleButtonIndexOption.value"
              >{{ chartStyleButtonIndexOption.title }}</option>
            </select>
          </div>
          <div class="ui-mini-container">
            <label
              class="sc3-form-label"
              for="chart-style-button-modal-chart-style-button-label-input"
            >LABEL</label>
            <br />
            <input
              id="chart-style-button-modal-chart-style-button-label-input"
              type="text"
              class="sc3-text-input"
              :class="{
                'sc3-text-input-invalid': !isChartStyleButtonLabelValid
              }"
              v-model="chartStyleButtonLabel"
              autocomplete="off"
              :disabled="isSavingStyleButton || isClearingStyleButton"
            />
          </div>
          <fieldset class="ui-mini-container">
            <legend class="sc3-form-label">COLOR</legend>
            <div class="chart-style-button-color-option-group">
              <label
                v-for="chartStyleButtonBackgroundColorOption in CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS"
                :key="chartStyleButtonBackgroundColorOption.hexCode"
                :title="chartStyleButtonBackgroundColorOption.title"
                class="chart-style-button-color-option"
                :class="{
                  'selected-chart-style-button-background-color-option': selectedChartStyleButtonBackgroundColorHexCode === chartStyleButtonBackgroundColorOption.hexCode,
                  'disabled-chart-style-button-background-color-option': isSavingStyleButton || isClearingStyleButton
                }"
              >
                <input
                  type="radio"
                  v-model="selectedChartStyleButtonBackgroundColorHexCode"
                  :value="chartStyleButtonBackgroundColorOption.hexCode"
                  :disabled="isSavingStyleButton || isClearingStyleButton"
                />
                <span
                  class="chart-style-button-color-swatch"
                  :style="{
                    backgroundColor: chartStyleButtonBackgroundColorOption.hexCode,
                    outlineColor: chartStyleButtonBackgroundColorOption.hexCode
                  }"
                ></span>
              </label>
            </div>
          </fieldset>
        </div>
        <label
          class="sc3-form-label"
          for="chart-style-button-modal-chart-style-select-menu"
        >SELECT CHARTSTYLE</label>
        <select
          id="chart-style-button-modal-chart-style-select-menu"
          class="sc3-select-menu"
          v-model="selectedChartStyleRecid"
          :disabled="isSavingStyleButton || isClearingStyleButton"
        >
          <option
            :value="CHART_STYLE_OPTION_SELECT_A_CHART_STYLE"
            disabled
          >-- Select a ChartStyle --</option>
          <option
            v-for="chartStyle in savedChartsStore.chartStyles"
            :key="chartStyle.recid"
            :value="chartStyle.recid"
            :disabled="chartStyle.chartStyleButtonIndex >= 0 && (!chartStyleBeingConfiguredInChartStyleButtonModal || chartStyleBeingConfiguredInChartStyleButtonModal.recid !== chartStyle.recid)"
          >{{ chartStyle.htmlSanitizedName }}</option>
        </select>
      </div>
      <hr class="sc3-form-divider" />
      <div v-show="errorMessage" class="error-message">{{ errorMessage }}</div>
      <button
        type="submit"
        class="sc3-button sc3-button-green"
        :disabled="isSavingStyleButton || isClearingStyleButton"
      >
        <i v-show="!isSavingStyleButton" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isSavingStyleButton" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Save StyleButton
      </button>
      <button
        v-if="chartStyleBeingConfiguredInChartStyleButtonModal && chartStyleButtonIndex !== POSITION_OPTION_SELECT_A_POSITION"
        type="button"
        @click="handleClearChartStyleButton()"
        class="sc3-button sc3-button-transparent-and-grey"
      >
        <i v-show="!isClearingStyleButton" class="fas fa-fw fa-times-circle"></i>
        <i v-show="isClearingStyleButton" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Clear StyleButton #{{ chartStyleButtonIndex + 1 }}
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useModals } from '@/composables/useModals';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useChartStyleButtons } from '@/composables/useChartStyleButtons';
  import { refreshChartStyles } from '@/composables/refreshChartStyles';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import { INVALID_CHART_STYLE_RECID, isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { modalType } = storeToRefs(layoutStore);
  const { CHART_STYLE_BUTTON_MODAL, closeModal } = useModals();
  const { addGreenActionResultMessage, addRedActionResultMessage } = useActionResultMessages();
  const {
    MAX_NUMBER_OF_CHART_STYLE_BUTTONS,
    CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS,
    DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION,
    CHART_STYLE_BUTTON_INDEX_OPTIONS,
    MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS,
    addChartStyleButton,
    removeChartStyleButton,
    updateChartStyleButtonChartStyle,
    updateChartStyleButton
  } = useChartStyleButtons();
  const POSITION_OPTION_SELECT_A_POSITION = -1;
  const CHART_STYLE_OPTION_SELECT_A_CHART_STYLE = INVALID_CHART_STYLE_RECID;
  const isInitializingModal = ref(true);
  const isSavingStyleButton = ref(false);
  const isClearingStyleButton = ref(false);
  const chartStyleButtonIndex = ref(POSITION_OPTION_SELECT_A_POSITION);
  const selectedChartStyleButtonIndex = ref(POSITION_OPTION_SELECT_A_POSITION);
  const selectedChartStyleRecid = ref(CHART_STYLE_OPTION_SELECT_A_CHART_STYLE);
  const selectedChartStyleButtonBackgroundColorHexCode = ref(DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.hexCode);
  const chartStyleButtonLabel = ref('');
  const errorMessage = ref('');
  let lastModalOpenedTime = -1;

  const chartStyleBeingConfiguredInChartStyleButtonModal = computed(() => {
    if (chartStyleButtonIndex.value === POSITION_OPTION_SELECT_A_POSITION) {
      return null;
    }

    return savedChartsStore.chartStyleButtonChartStyles.at(chartStyleButtonIndex.value) ?? null;
  });

  const isAddingNewChartStyleButton = computed(() => {
    return !chartStyleBeingConfiguredInChartStyleButtonModal.value || !isValidChartStyleRecid(chartStyleBeingConfiguredInChartStyleButtonModal.value.recid, false);
  });

  const modalTitle = computed(() => {
    if (isAddingNewChartStyleButton.value) {
      return 'Add StyleButton';
    }

    if (chartStyleButtonIndex.value === POSITION_OPTION_SELECT_A_POSITION) {
      return 'Edit StyleButton';
    }

    return `Edit StyleButton #${chartStyleButtonIndex.value + 1}`;
  });

  const availableChartStyleButtonIndexOptions = computed(() => {
    return CHART_STYLE_BUTTON_INDEX_OPTIONS.value.slice(0, savedChartsStore.chartStyleButtonChartStyles.length + 1);
  });

  const formattedChartStyleButtonLabel = computed(() => {
    return chartStyleButtonLabel.value.replace(/\s/g, '');
  });

  const isChartStyleButtonLabelValid = computed(() => {
    return formattedChartStyleButtonLabel.value.length <= MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS;
  });

  interface ChartStyleButtonModalState {
    modalType: string;
    lastModalOpenedTime: number;
  }

  async function handleClearChartStyleButton() {
    if (
      isInitializingModal.value ||
      isSavingStyleButton.value ||
      isClearingStyleButton.value ||
      modalType.value !== CHART_STYLE_BUTTON_MODAL
    ) {
      return;
    }

    isClearingStyleButton.value = true;

    clearErrorMessage();

    const modalState = getModalState();

    await refreshChartStyles();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    const chartStyleRecidBeingConfiguredInChartStyleButtonModal = chartStyleBeingConfiguredInChartStyleButtonModal.value?.recid ?? INVALID_CHART_STYLE_RECID;
    const chartStyle = savedChartsStore.chartStyles.find(cs => cs.recid === chartStyleRecidBeingConfiguredInChartStyleButtonModal) ?? null;

    if (!chartStyle) {
      window.sc3WorkbenchLogger.error(`Unable to clear ChartStyle button from ChartStyleButtonModal - no ChartStyle was found with recid: ${chartStyleRecidBeingConfiguredInChartStyleButtonModal}.`);
      await handleChartStyleButtonOperationCompleted(false, 'clear', modalState);
      return;
    }

    let wasSuccessful = false;

    try {
      await removeChartStyleButton(chartStyle.recid);

      wasSuccessful = true;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to clear ChartStyle button from ChartStyleButtonModal - error occurred while deleting ChartStyle button with recid: ${chartStyleRecidBeingConfiguredInChartStyleButtonModal}.`, e);
    }

    await handleChartStyleButtonOperationCompleted(wasSuccessful, 'clear', modalState);
  }

  async function handleChartStyleButtonFormSubmit() {
    if (
      isInitializingModal.value ||
      isSavingStyleButton.value ||
      isClearingStyleButton.value ||
      modalType.value !== CHART_STYLE_BUTTON_MODAL
    ) {
      return;
    }

    isSavingStyleButton.value = true;

    clearErrorMessage();

    const modalState = getModalState();

    await refreshChartStyles();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    const selectedChartStyleButtonIndexOption = availableChartStyleButtonIndexOptions.value.find(option => option.value === selectedChartStyleButtonIndex.value) ?? null;
    const selectedChartStyle = savedChartsStore.chartStyles.find(cs => cs.recid === selectedChartStyleRecid.value) ?? null;
    const selectedChartStyleButtonBackgroundColorOption = CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS.find(option => option.hexCode === selectedChartStyleButtonBackgroundColorHexCode.value) ?? null;

    if (!selectedChartStyleButtonIndexOption) {
      selectedChartStyleButtonIndex.value = POSITION_OPTION_SELECT_A_POSITION;
      errorMessage.value = 'Please select a button position.';
      isSavingStyleButton.value = false;
      return;
    }

    if (!isChartStyleButtonLabelValid.value) {
      errorMessage.value = `Label must be between 0 and ${MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS} characters.`;
      isSavingStyleButton.value = false;
      return;
    }

    if (
      !selectedChartStyle ||
      (selectedChartStyle.chartStyleButtonIndex >= 0 && (!chartStyleBeingConfiguredInChartStyleButtonModal.value || selectedChartStyle.recid !== chartStyleBeingConfiguredInChartStyleButtonModal.value.recid))
    ) {
      selectedChartStyleRecid.value = CHART_STYLE_OPTION_SELECT_A_CHART_STYLE;
      errorMessage.value = 'Please select a ChartStyle.';
      isSavingStyleButton.value = false;
      return;
    }

    if (!selectedChartStyleButtonBackgroundColorOption) {
      selectedChartStyleButtonBackgroundColorHexCode.value = DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.hexCode;
      errorMessage.value = 'Please select a button color.';
      isSavingStyleButton.value = false;
      return;
    }

    const newRecid = selectedChartStyle.recid;
    const oldRecid = chartStyleBeingConfiguredInChartStyleButtonModal.value?.recid ?? INVALID_CHART_STYLE_RECID;
    const positionIndex = selectedChartStyleButtonIndexOption.value;
    const hexCode = selectedChartStyleButtonBackgroundColorOption.hexCode;
    const label = formattedChartStyleButtonLabel.value;
    const isAddingNewChartStyleButtonCopy = isAddingNewChartStyleButton.value;
    let wasSuccessful = false;

    try {
      if (isAddingNewChartStyleButtonCopy) {
        await addChartStyleButton(newRecid, positionIndex, hexCode, label);
      } else {
        if (newRecid !== oldRecid) {
          await updateChartStyleButtonChartStyle(newRecid, oldRecid);
        }

        await updateChartStyleButton(newRecid, positionIndex, hexCode, label);
      }

      wasSuccessful = true;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to configure ChartStyle button from ChartStyleButtonModal - error occurred for isAddingNewChartStyleButtonCopy: ${isAddingNewChartStyleButtonCopy} and newRecid: ${newRecid} and oldRecid: ${oldRecid} and positionIndex: ${positionIndex} and hexCode: ${hexCode} and label: ${label}.`, e);
    }

    await handleChartStyleButtonOperationCompleted(wasSuccessful, isAddingNewChartStyleButtonCopy ? 'add' : 'save', modalState);
  }

  async function handleChartStyleButtonOperationCompleted(wasSuccessful: boolean, actionVerb: 'add' | 'clear' | 'save', modalState: ChartStyleButtonModalState) {
    await refreshChartStyles();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    let actionVerbPastTense = '';

    switch (actionVerb) {
      case 'add':
        actionVerbPastTense = 'added';
        break;
      case 'clear':
        actionVerbPastTense = 'cleared';
        break;
      case 'save':
      default:
        actionVerbPastTense = 'saved';
        break;
    }

    if (wasSuccessful) {
      addGreenActionResultMessage(`Success! Your StyleButton has been ${actionVerbPastTense}!`);
    } else {
      addRedActionResultMessage(`Failed to ${actionVerb} StyleButton. Please try again.`);
    }

    closeModal();
  }

  function getModalState(): ChartStyleButtonModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime
    };
  }

  function hasModalStateChanged(modalState: ChartStyleButtonModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  function resetModalState() {
    isInitializingModal.value = false;
    isSavingStyleButton.value = false;
    isClearingStyleButton.value = false;
    chartStyleButtonIndex.value = POSITION_OPTION_SELECT_A_POSITION;
    selectedChartStyleButtonIndex.value = POSITION_OPTION_SELECT_A_POSITION;
    selectedChartStyleRecid.value = CHART_STYLE_OPTION_SELECT_A_CHART_STYLE;
    selectedChartStyleButtonBackgroundColorHexCode.value = DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.hexCode;
    chartStyleButtonLabel.value = '';

    clearErrorMessage();
  }

  function getChartStyleButtonIndexBeingConfiguredInChartStyleButtonModal() {
    const existingChartStyleButtonIndex = savedChartsStore.chartStyleButtonChartStyles.findIndex(chartStyle => chartStyle.recid === savedChartsStore.chartStyleRecidBeingConfiguredInChartStyleButtonModal);

    if (existingChartStyleButtonIndex >= 0) {
      return existingChartStyleButtonIndex;
    }

    const nextAvailableChartStyleButtonIndex = savedChartsStore.chartStyleButtonChartStyles.length;

    if (nextAvailableChartStyleButtonIndex < MAX_NUMBER_OF_CHART_STYLE_BUTTONS) {
      return nextAvailableChartStyleButtonIndex;
    }

    return POSITION_OPTION_SELECT_A_POSITION;
  }

  async function handleChartStyleButtonModalOpened() {
    resetModalState();

    isInitializingModal.value = true;
    const modalState = getModalState();

    await refreshChartStyles();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    prefillModalUIFields();

    isInitializingModal.value = false;
  }

  function handleChartStyleButtonModalClosed() {
    savedChartsStore.$patch({ chartStyleRecidBeingConfiguredInChartStyleButtonModal: INVALID_CHART_STYLE_RECID });
  }

  function prefillModalUIFields() {
    chartStyleButtonIndex.value = getChartStyleButtonIndexBeingConfiguredInChartStyleButtonModal();
    selectedChartStyleButtonIndex.value = chartStyleButtonIndex.value;
    const selectedChartStyle = chartStyleBeingConfiguredInChartStyleButtonModal.value;
    const alternateSelectedChartStyle = savedChartsStore.chartStyles.find(chartStyle => chartStyle.recid === savedChartsStore.chartStyleRecidBeingConfiguredInChartStyleButtonModal) ?? null;
    selectedChartStyleRecid.value = selectedChartStyle?.recid ?? alternateSelectedChartStyle?.recid ?? CHART_STYLE_OPTION_SELECT_A_CHART_STYLE;
    selectedChartStyleButtonBackgroundColorHexCode.value = selectedChartStyle?.chartStyleButtonBackgroundColorHexCode ?? DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.hexCode;
    chartStyleButtonLabel.value = selectedChartStyle?.chartStyleButtonLabel ?? '';
  }

  async function handleModalTypeChanged() {
    if (modalType.value !== CHART_STYLE_BUTTON_MODAL) {
      handleChartStyleButtonModalClosed();
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleChartStyleButtonModalOpened();
  }

  function clearErrorMessage() {
    errorMessage.value = '';
  }

  watch(selectedChartStyleButtonIndex, clearErrorMessage);
  watch(selectedChartStyleRecid, clearErrorMessage);
  watch(selectedChartStyleButtonBackgroundColorHexCode, clearErrorMessage);
  watch(chartStyleButtonLabel, clearErrorMessage);
  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .loading-spinner-container,
  .chart-style-button-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .chart-style-button-form-ui {
    text-align: left;
    font-size: 0px;
  }

  #chart-style-button-modal-chart-style-button-position-select-menu {
    max-width: 70px;
  }

  #chart-style-button-modal-chart-style-button-label-input {
    max-width: 60px;
  }

  .error-message {
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    font-weight: 700;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }

  .ui-mini-container {
    display: inline-block;
    vertical-align: top;
    margin: 0px 12px 0px 0px;
  }

  .ui-mini-container:last-child {
    margin: 0px;
  }

  .ui-mini-container label,
  .ui-mini-container select {
    display: inline-block;
    vertical-align: top;
    width: unset;
  }
</style>
